import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Grid3X3, X, Lock, Snowflake, Flame, Check } from 'lucide-react';
import { useEditorStore } from '../../store/editorStore';
import {
  generateRowRacks,
  generatePairedRow,
  detectAisleType,
  suggestNextRow,
} from '../../utils/rowGenerator.logic';
import { useShallow } from 'zustand/react/shallow';

interface Props {
  onClose: () => void;
}

const FIELD: React.CSSProperties = {
  background: 'var(--bg-panel, #111827)',
  border: '1px solid var(--border-color, #374151)',
  borderRadius: 6,
  color: 'var(--text-primary, #f3f4f6)',
  padding: '6px 10px',
  fontSize: 12,
  width: '100%',
  outline: 'none',
  boxSizing: 'border-box',
};

const LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: 'var(--text-secondary, #9ca3af)',
  marginBottom: 4,
  display: 'block',
};

const GRID2: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
  marginBottom: 12,
};

export const RowGeneratorModal: React.FC<Props> = ({ onClose }) => {
  const {
    rows,
    racks,
    stageX,
    stageY,
    stageScale,
    stageW,
    stageH,
    dcSettings,
    addGeneratedRowBatch,
  } = useEditorStore(useShallow(state => ({
    rows: state.rows,
    racks: state.racks,
    stageX: state.stageX,
    stageY: state.stageY,
    stageScale: state.stageScale,
    stageW: state.stageW,
    stageH: state.stageH,
    dcSettings: state.dcSettings,
    addGeneratedRowBatch: state.addGeneratedRowBatch,
  })));

  // Viewport center snapped to 600mm
  const defaultPos = useMemo(() => {
    const scale = Math.max(0.1, stageScale);
    const cx_px = (-stageX + stageW / 2) / scale;
    const cy_px = (-stageY + stageH / 2) / scale;
    const x_m = Math.max(0.6, Math.round((cx_px / 100) / 0.6) * 0.6);
    const y_m = Math.max(0.6, Math.round((cy_px / 100) / 0.6) * 0.6);
    return {
      x: isFinite(x_m) ? Number(x_m.toFixed(1)) : 1.2,
      y: isFinite(y_m) ? Number(y_m.toFixed(1)) : 1.2,
    };
  }, [stageX, stageY, stageScale, stageW, stageH]);

  const suggested = useMemo(() => suggestNextRow(rows, racks), [rows, racks]);

  const [rowName, setRowName] = useState(suggested.suggestedName);
  const [rackCount, setRackCount] = useState(8);
  const [direction, setDirection] = useState<'horizontal' | 'vertical'>('horizontal');
  const [sequenceDirection, setSequenceDirection] = useState<'forward' | 'reverse'>('forward');
  const [orientation, setOrientation] = useState<number>(0);
  const [startX_m, setStartX_m] = useState(defaultPos.x);
  const [startY_m, setStartY_m] = useState(defaultPos.y);
  const [rackWidthM, setRackWidthM] = useState(0.6);
  const [rackDepthM, setRackDepthM] = useState(1.0);

  // Corridor & Paired Row
  const [createPairedRow, setCreatePairedRow] = useState(true);
  const [aisleWidthM, setAisleWidthM] = useState(1.2);
  const [isContained, setIsContained] = useState(true);
  const [aisleType, setAisleType] = useState<'auto' | 'cold' | 'hot' | 'containment'>('auto');

  // Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Derived preview
  const preview = useMemo(() => {
    const pairedOrient = (orientation + 180) % 360;
    const detected = detectAisleType(
      orientation,
      pairedOrient,
      direction === 'horizontal' ? 'below' : 'right'
    );
    const finalAisle = isContained ? 'containment' : (aisleType === 'auto' ? detected : aisleType);

    const totalRacks = createPairedRow ? rackCount * 2 : rackCount;
    const totalU = totalRacks * 42;
    const rowLen_m = (rackCount * rackWidthM).toFixed(1);
    const rowDepth_m = createPairedRow
      ? (rackDepthM * 2 + aisleWidthM).toFixed(1)
      : rackDepthM.toFixed(1);

    const common = {
      rowName: rowName.trim() || suggested.suggestedName,
      labelIndex: suggested.labelIndex,
      startX_m,
      startY_m,
      rackCount,
      rackWidth_m: rackWidthM,
      rackDepth_m: rackDepthM,
      orientation,
      direction,
      sequenceDirection,
      namingPattern: dcSettings.namingMask,
      module: dcSettings.module,
      hall: dcSettings.hall,
      namingProfiles: dcSettings.namingProfiles,
      namingAssignments: dcSettings.namingAssignments,
    } as const;
    let addressRows: Array<{ name: string; labels: string[]; head: string; tail: string }> = [];
    try {
      if (createPairedRow) {
        const result = generatePairedRow({ ...common, aisleWidth_m: aisleWidthM, aisleType: aisleType === 'auto' ? undefined : aisleType, isContained });
        addressRows = [result.primaryRow, result.pairedRow].map((row, index) => ({
          name: row.name,
          labels: (index === 0 ? result.primaryRacks : result.pairedRacks).slice(0, 5).map(rack => rack.label),
          head: `${row.head.xM.toFixed(1)}, ${row.head.yM.toFixed(1)}`,
          tail: `${row.tail.xM.toFixed(1)}, ${row.tail.yM.toFixed(1)}`,
        }));
      } else {
        const result = generateRowRacks(common);
        addressRows = [{
          name: result.row.name,
          labels: result.racks.slice(0, 5).map(rack => rack.label),
          head: `${result.row.head.xM.toFixed(1)}, ${result.row.head.yM.toFixed(1)}`,
          tail: `${result.row.tail.xM.toFixed(1)}, ${result.row.tail.yM.toFixed(1)}`,
        }];
      }
    } catch {
      addressRows = [];
    }

    return {
      detected,
      finalAisle,
      totalRacks,
      totalU,
      rowLen_m,
      rowDepth_m,
      addressRows,
    };
  }, [orientation, direction, sequenceDirection, isContained, aisleType, createPairedRow, rackCount, rackWidthM, rackDepthM, aisleWidthM, rowName, suggested, startX_m, startY_m, dcSettings]);

  const handleCreate = useCallback(() => {
    if (rackCount < 1) return;

    if (createPairedRow) {
      const result = generatePairedRow({
        rowName: rowName.trim() || suggested.suggestedName,
        labelIndex: suggested.labelIndex,
        startX_m,
        startY_m,
        rackCount,
        rackWidth_m: rackWidthM,
        rackDepth_m: rackDepthM,
        orientation,
        direction,
        sequenceDirection,
        namingPattern: dcSettings.namingMask,
        module: dcSettings.module,
        hall: dcSettings.hall,
        namingProfiles: dcSettings.namingProfiles,
        namingAssignments: dcSettings.namingAssignments,
        aisleWidth_m: aisleWidthM,
        aisleType: aisleType === 'auto' ? undefined : aisleType,
        isContained,
      });

      addGeneratedRowBatch({
        rows: [result.primaryRow, result.pairedRow],
        racks: [...result.primaryRacks, ...result.pairedRacks] as any,
        aisleZone: result.aisleZone as any,
      });
    } else {
      const result = generateRowRacks({
        rowName: rowName.trim() || suggested.suggestedName,
        labelIndex: suggested.labelIndex,
        startX_m,
        startY_m,
        rackCount,
        rackWidth_m: rackWidthM,
        rackDepth_m: rackDepthM,
        orientation,
        direction,
        sequenceDirection,
        namingPattern: dcSettings.namingMask,
        module: dcSettings.module,
        hall: dcSettings.hall,
        namingProfiles: dcSettings.namingProfiles,
        namingAssignments: dcSettings.namingAssignments,
      });

      addGeneratedRowBatch({
        rows: [result.row],
        racks: result.racks as any,
      });
    }

    onClose();
  }, [
    rackCount,
    createPairedRow,
    rowName,
    suggested,
    startX_m,
    startY_m,
    rackWidthM,
    rackDepthM,
    orientation,
    direction,
    sequenceDirection,
    dcSettings,
    aisleWidthM,
    aisleType,
    isContained,
    addGeneratedRowBatch,
    onClose,
  ]);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.75)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        background: '#0d1117',
        border: '1px solid #1e293b',
        borderRadius: 12,
        width: 560,
        maxHeight: '92vh',
        overflow: 'auto',
        boxShadow: '0 24px 64px rgba(0, 0, 0, 0.85)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #1e293b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary, #f3f4f6)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Grid3X3 size={18} style={{ color: 'var(--accent, #38bdf8)' }} /> Генератор рядов и коридоров
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted, #94a3b8)', marginTop: 2 }}>
              Стандарт ASHRAE TC 9.9 • Шаг кратности плитке 600 мм
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Закрыть"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-muted, #64748b)',
              padding: 4,
              borderRadius: 4,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Section: Row Params */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
              Параметры ряда
            </div>
            <div style={GRID2}>
              <div>
                <label style={LABEL}>Имя ряда</label>
                <input
                  style={FIELD}
                  type="text"
                  value={rowName}
                  onChange={e => setRowName(e.target.value)}
                  placeholder="Ряд A"
                />
              </div>
              <div>
                <label style={LABEL}>Количество стоек</label>
                <input
                  style={FIELD}
                  type="number"
                  min={1}
                  max={50}
                  value={rackCount}
                  onChange={e => setRackCount(Math.max(1, parseInt(e.target.value) || 1))}
                />
              </div>
            </div>

            <div style={GRID2}>
              <div>
                <label style={LABEL}>Направление протягивания</label>
                <select
                  style={FIELD}
                  value={direction}
                  onChange={e => {
                    const newDir = e.target.value as 'horizontal' | 'vertical';
                    setDirection(newDir);
                    if (newDir === 'vertical' && (orientation === 0 || orientation === 180)) {
                      setOrientation(270);
                    } else if (newDir === 'horizontal' && (orientation === 90 || orientation === 270)) {
                      setOrientation(0);
                    }
                  }}
                >
                  <option value="horizontal">Горизонтально (вправо →)</option>
                  <option value="vertical">Вертикально (вниз ↓)</option>
                </select>
              </div>
              <div>
                <label style={LABEL}>Ориентация лицевой стороны (Front)</label>
                <select
                  style={FIELD}
                  value={orientation}
                  onChange={e => setOrientation(parseInt(e.target.value))}
                >
                  <option value={0}>0° — Юг (лицо вниз ↓, холодный приток)</option>
                  <option value={180}>180° — Север (лицо вверх ↑, холодный приток)</option>
                  <option value={90}>90° — Запад (лицо влево ←)</option>
                  <option value={270}>270° — Восток (лицо вправо →)</option>
                </select>
              </div>
            </div>

            <div style={{ ...GRID2, marginBottom: 0 }}>
              <div>
                <label style={LABEL}>Порядок нумерации в ряду</label>
                <select
                  style={FIELD}
                  value={sequenceDirection}
                  onChange={e => setSequenceDirection(e.target.value as 'forward' | 'reverse')}
                >
                  <option value="forward">От начала к концу</option>
                  <option value="reverse">От конца к началу</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'end', fontSize: 10, color: '#64748b', lineHeight: 1.35 }}>
                Номера остаются 1-based для оператора, физическое направление сохраняется в ряду.
              </div>
            </div>

            <div style={GRID2}>
              <div>
                <label style={LABEL}>
                  Начало X (м) <span style={{ color: '#64748b' }}>({Math.round(startX_m / 0.6)} пл.)</span>
                </label>
                <input
                  style={FIELD}
                  type="number"
                  step={0.6}
                  value={startX_m}
                  onChange={e => setStartX_m(parseFloat(e.target.value) || 0)}
                />
              </div>
              <div>
                <label style={LABEL}>
                  Начало Y (м) <span style={{ color: '#64748b' }}>({Math.round(startY_m / 0.6)} пл.)</span>
                </label>
                <input
                  style={FIELD}
                  type="number"
                  step={0.6}
                  value={startY_m}
                  onChange={e => setStartY_m(parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
          </div>

          {/* Section: Dimensions */}
          <div style={{ background: '#111827', padding: '12px 14px', borderRadius: 8, border: '1px solid #1f2937' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
              Габариты стойки
            </div>
            <div style={GRID2}>
              <div>
                <label style={LABEL}>Ширина стойки (м)</label>
                <select
                  style={FIELD}
                  value={rackWidthM}
                  onChange={e => setRackWidthM(parseFloat(e.target.value))}
                >
                  <option value={0.6}>0.6 м (Стандарт 600 мм — 1 плитка)</option>
                  <option value={0.8}>0.8 м (Сетевая 800 мм)</option>
                </select>
              </div>
              <div>
                <label style={LABEL}>Глубина стойки (м)</label>
                <select
                  style={FIELD}
                  value={rackDepthM}
                  onChange={e => setRackDepthM(parseFloat(e.target.value))}
                >
                  <option value={1.0}>1.0 м (1000 мм)</option>
                  <option value={1.2}>1.2 м (1200 мм — глубокая серверная)</option>
                  <option value={0.8}>0.8 м (800 мм — телекоммуникационная)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section: Paired row and corridor */}
          <div style={{ background: '#111827', padding: '12px 14px', borderRadius: 8, border: '1px solid #1f2937' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#f3f4f6' }}
                title="По стандарту ASHRAE TC 9.9 коридор — это пространство между двумя противоположно направленными рядами (лицом к лицу = холодный, тылом к тылу = горячий). Снимите чекбокс, чтобы создать один ряд без парного."
              >
                <input
                  type="checkbox"
                  checked={createPairedRow}
                  onChange={e => setCreatePairedRow(e.target.checked)}
                  style={{ accentColor: '#3b82f6', width: 16, height: 16 }}
                />
                Создать парный ряд с коридором (Aisle)
              </label>
            </div>
            <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 10, lineHeight: 1.4 }}>
              По ASHRAE TC 9.9 коридор — пространство <em>между</em> двумя рядами. При включении создаётся зеркальный ряд и зона коридора между ними. Снимите, если нужен одиночный ряд.
            </div>

            {createPairedRow && (
              <>
                <div style={GRID2}>
                  <div>
                    <label style={LABEL}>Ширина коридора</label>
                    <select
                      style={FIELD}
                      value={aisleWidthM}
                      onChange={e => setAisleWidthM(parseFloat(e.target.value))}
                    >
                      <option value={0.6}>0.6 м (1 плитка фальшпола)</option>
                      <option value={1.2}>1.2 м (2 плитки — стандарт ASHRAE)</option>
                      <option value={1.8}>1.8 м (3 плитки — просторный проход)</option>
                    </select>
                  </div>
                  <div>
                    <label style={LABEL}>Тип коридора</label>
                    <select
                      style={FIELD}
                      value={aisleType}
                      onChange={e => setAisleType(e.target.value as any)}
                    >
                      <option value="auto">Авто по ориентации ({preview.detected === 'cold' ? 'Холодный' : 'Горячий'})</option>
                      <option value="cold">Холодный коридор (Cold Aisle)</option>
                      <option value="hot">Горячий коридор (Hot Aisle)</option>
                      <option value="containment">Изолированный (Containment)</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginTop: 4 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: '#c084fc' }}>
                    <input
                      type="checkbox"
                      checked={isContained}
                      onChange={e => setIsContained(e.target.checked)}
                      style={{ accentColor: '#a855f7', width: 15, height: 15 }}
                    />
                    Изоляция коридора (Containment: крыша и торцевые двери)
                  </label>
                </div>
              </>
            )}

            <div style={{ marginTop: 10, paddingTop: 9, borderTop: '1px solid #1f2937', fontSize: 10 }}>
              <div style={{ color: '#cbd5e1', fontWeight: 600, marginBottom: 5 }}>Адреса до применения</div>
              {preview.addressRows.map(row => (
                <div key={row.name} style={{ marginBottom: 5 }}>
                  <div style={{ color: '#94a3b8' }}>{row.name} · {sequenceDirection === 'reverse' ? 'обратный порядок' : 'прямой порядок'}</div>
                  <div style={{ color: '#60a5fa', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.labels.join('  ')}{rackCount > 5 ? `  … +${rackCount - 5}` : ''}
                  </div>
                  <div style={{ color: '#64748b' }}>голова ({row.head}) → хвост ({row.tail})</div>
                </div>
              ))}
            </div>
          </div>

          {/* Specification Preview */}
          <div style={{
            background: 'rgba(59, 130, 246, 0.05)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 12,
            color: '#94a3b8',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#e2e8f0', fontWeight: 600 }}>
              <span>Итого объектов:</span>
              <span style={{ color: '#60a5fa' }}>{preview.totalRacks} стоек (42U)</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Полезная IT-ёмкость:</span>
              <span style={{ color: '#22c55e', fontWeight: 600 }}>{preview.totalU} U</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Габариты пятна застройки:</span>
              <span style={{ color: '#f3f4f6' }}>{preview.rowLen_m} × {preview.rowDepth_m} м</span>
            </div>
            {createPairedRow && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Коридор:</span>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  color: isContained ? '#c084fc' : preview.detected === 'cold' ? '#38bdf8' : '#f97316',
                  fontWeight: 600,
                }}>
                  {isContained ? <Lock size={12} /> : preview.detected === 'cold' ? <Snowflake size={12} /> : <Flame size={12} />}
                  {isContained ? 'Изолированный' : preview.detected === 'cold' ? 'Холодный' : 'Горячий'} ({aisleWidthM} м)
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid #1e293b',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 10,
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '7px 16px',
              borderRadius: 6,
              border: '1px solid #374151',
              background: 'transparent',
              color: '#94a3b8',
              fontSize: 12,
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            Отмена
          </button>
          <button
            onClick={handleCreate}
            style={{
              padding: '7px 20px',
              borderRadius: 6,
              border: 'none',
              background: '#2563eb',
              color: '#ffffff',
              fontSize: 12,
              cursor: 'pointer',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Check size={14} /> Создать {createPairedRow ? 'ряды' : 'ряд'}
          </button>
        </div>
      </div>
    </div>
  );
};
